import * as React from "react";
import { AlertCircle, Inbox, Loader2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/misc";
import { ApiError } from "@/lib/api";
import { env } from "@/lib/env";
import { statusLabel } from "@/lib/labels";
import { cn } from "@/lib/utils";

export function PageHeader({
  title,
  description,
  actions,
  back,
}: {
  title: React.ReactNode;
  description?: React.ReactNode;
  actions?: React.ReactNode;
  back?: React.ReactNode;
}) {
  return (
    <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
      <div className="min-w-0">
        {back}
        <h1 className="truncate text-xl font-semibold tracking-tight">{title}</h1>
        {description && <p className="mt-1 text-sm text-muted-foreground">{description}</p>}
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
    </div>
  );
}

export function StatusBadge({ status }: { status: string | null | undefined }) {
  const [text, variant] = statusLabel(status);
  return <Badge variant={variant}>{text}</Badge>;
}

/** Hata kodunu okunur metne çevirir (API yalnızca kod döner) */
export function errorText(e: unknown): string {
  if (e instanceof ApiError) {
    if (e.status === 403 && e.reason === "mfa_required")
      return "Bu işlem için iki adımlı doğrulama gerekiyor.";
    if (e.status === 403 && e.reason === "self_change")
      return "Kendi yetkinizi ya da durumunuzu değiştiremezsiniz; başka bir yöneticiden isteyin.";
    if (e.status === 403 && e.reason === "own_request")
      return "Kendi isteğinizi onaylayamazsınız; ikinci bir yönetici onaylamalı.";
    if (e.status === 403) return "Bu işlem için yetkiniz yok.";
    if (e.status === 404) return "Kayıt bulunamadı.";
    if (e.status === 503 && e.reason === "invite_unavailable")
      return "Davet e-postası gönderilemiyor: API'de SUPABASE_SERVICE_ROLE_KEY boş. Local: pnpm env:local, sonra API'yi yeniden başlatın.";
    if (e.status === 409 && e.reason === "last_super_admin")
      return "En az bir aktif super admin kalmalı.";
    if (e.status === 409 && e.reason === "already_staff") return "Bu kişi zaten ekipte.";
    if (e.status === 409)
      return `Kayıt bu arada değişmiş olabilir (${e.reason ?? "çakışma"}). Sayfayı yenileyin.`;
    if (e.status === 422) return `İşlem kurala uymuyor: ${e.reason ?? "doğrulama"}.`;
    if (e.status === 429) return "Çok fazla istek. Biraz bekleyip tekrar deneyin.";
    if (e.status === 400) return "Gönderilen bilgiler eksik ya da hatalı.";
    if (e.status >= 500) return "Sunucu hatası. Biraz sonra tekrar deneyin.";
    return e.message;
  }
  if (e instanceof TypeError)
    return `API'ye ulaşılamıyor (${env.apiUrl}). API çalışıyor mu? Terminalde: pnpm --filter @poji/api dev`;
  return e instanceof Error ? e.message : String(e);
}

export function ErrorState({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  return (
    <div className="flex flex-col items-center gap-3 rounded-xl border border-dashed p-10 text-center">
      <AlertCircle className="size-6 text-destructive" />
      <p className="text-sm">{errorText(error)}</p>
      {onRetry && (
        <Button variant="outline" size="sm" onClick={onRetry}>
          Tekrar dene
        </Button>
      )}
    </div>
  );
}

export function EmptyState({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex flex-col items-center gap-2 p-10 text-center text-sm text-muted-foreground">
      <Inbox className="size-6" />
      {children}
    </div>
  );
}

export function LoadingRows({ rows = 5 }: { rows?: number }) {
  return (
    <div className="space-y-2 p-4" aria-label="Yükleniyor">
      {Array.from({ length: rows }, (_, i) => (
        <Skeleton key={i} className="h-9 w-full" />
      ))}
    </div>
  );
}

export function Spinner({ className }: { className?: string }) {
  return <Loader2 className={cn("size-4 animate-spin", className)} />;
}

/** Etiket–değer listesi (ayrıntı sayfaları) */
export function KeyValues({
  items,
  className,
}: {
  items: [React.ReactNode, React.ReactNode][];
  className?: string;
}) {
  return (
    <dl
      className={cn(
        "grid grid-cols-1 gap-x-6 gap-y-3 text-sm sm:grid-cols-[minmax(8rem,auto)_1fr]",
        className,
      )}
    >
      {items.map(([k, v], i) => (
        <React.Fragment key={i}>
          <dt className="text-muted-foreground">{k}</dt>
          <dd className="min-w-0 break-words">{v ?? "—"}</dd>
        </React.Fragment>
      ))}
    </dl>
  );
}

export function Mono({ children }: { children: React.ReactNode }) {
  return <span className="font-mono text-xs text-muted-foreground">{children}</span>;
}
